import React, {FC, ReactNode, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {assertUnreachable} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {usePushEvent} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {useAppQuery} from '../../../../../libs/shared/core/src/services/query/useAppQuery';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonContent} from '../../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PushEventTypesEnum} from '../../../../../srcCommon/push/pushHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {saveUserPreferencesAction} from '../../../../store/data/preferences/preferencesActions';
import {getActivationPreferences} from '../../../../store/data/preferences/preferencesSelectors';
import {InfoBubble} from '../../../common/infoBubbles/infoBubble';
import {InfoBubbleContent} from '../../../common/infoBubbles/infoBubbleContent';
import {InfoBubbleDescription} from '../../../common/infoBubbles/infoBubbleContentDescription';
import {InfoBubblePill, InfoBubblePillVariantsEnum} from '../../../common/infoBubbles/infoBubblePill';
import {
  bestPracticesCourseTaskSetupGuideUuid,
  connectChannelTask,
  inboxesVsChannelsCourseTaskSetupGuideUuid,
  inviteTeammatesTask,
  sharedWorkspaceTask,
} from '../../../onboarding/admin/tasks/adminOnboardingTasks';
import {taskDefinitions} from '../../../onboarding/adminSetupGuide/adminSetupGuide';
import {
  AdminSetupGuideContainer,
  useTasks,
} from '../../../onboarding/adminSetupGuide/adminSetupGuideContainer';
import {
  AdminSetupGuideScreensEnum,
  AdminSetupGuideScreenTask,
  AdminSetupGuideTasksAndProgress,
} from '../../../onboarding/adminSetupGuide/adminSetupGuideTypes';
import {
  ApiCompletedCourse,
  lmsCompletedCoursesQueries,
} from '../../../onboarding/welcome/onboardingWelcomeContainer';
import {ContinueSetupTipsEnum, useOnboardingModalState} from './onboardingContext';
import {useSuspendedUntil} from './onboardingHelpers';

/*
 * Props.
 */

interface AdminSetupGuideButtonProps {
  className?: string;
  panelId: AppPanelId;
}

/*
 * Style.
 */

const StyledButton = styled(ChromelessButton)`
  // Disable drag in Electron.
  -webkit-app-region: no-drag;

  border-radius: 100px;
  overflow: hidden;
`;

const StyledButtonContent = styled(ButtonContent)`
  color: ${(p) => p.theme.topBar.buttonColor};
  background-color: transparent;

  ${hoverSelector} {
    color: ${(p) => p.theme.topBar.buttonHoverColor};
    background-color: ${(p) => p.theme.topBar.buttonHoverBackground};
  }
`;

const StyledLabelDiv = styled.div`
  ${ellipsis(true)};
`;

const StyledInfoBubble = styled(InfoBubble)`
  width: 380px;
`;

const StyledInfoBubbleTitleDiv = styled.div`
  margin-bottom: 4px;
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryLarge};
  line-height: 1.5;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledCloseButton = styled(ChromelessButton)`
  position: absolute;
  right: 7px;
  top: 7px;
  display: flex;
  width: 26px;
  height: 26px;
  padding: 5px;
  justify-content: center;
  align-items: center;
`;

const StyledCloseIcon = styled(Icon)`
  width: 16px;
  height: 16px;
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

const courseUuids = [inboxesVsChannelsCourseTaskSetupGuideUuid, bestPracticesCourseTaskSetupGuideUuid];

// TODO: This is much more than a button. Consider renaming to AdminSetupGuideButtonCoordinator or something.
export const AdminSetupGuideButton: FC<AdminSetupGuideButtonProps> = (props) => {
  const {panelId} = props;
  const {trackDataEvent} = useDataEventTracking();
  const dispatch = useAppDispatch();
  const tipAnchor = useRef<HTMLDivElement>(null);

  const {completedCourses, refetchCompletedCourses} = useQueryLmsCourses();
  const tasks = useTasks(taskDefinitions, completedCourses);
  const previousTasks = usePrevious(tasks);

  const {
    isAdminSetupGuideOpen,
    defaultAdminSetupGuideScreen,
    openAdminSetupGuideSetupScreen,
    openAdminSetupGuide,
    closeAdminSetupGuide,
  } = useOnboardingModalState();

  const tasksSuspendedUntilOpen = useSuspendedUntil(tasks, isAdminSetupGuideOpen, 500);

  const [isDismissPermanentlyDialogOpen, setIsDismissPermanentlyDialogOpen] = useState(false);

  const activationPreferences = useAppSelector(getActivationPreferences);

  const onAdminSetupGuideOpen = useEventHandler(() => {
    refetchCompletedCourses();
  });

  useEffect(() => {
    if (isAdminSetupGuideOpen) {
      onAdminSetupGuideOpen();
    }
  }, [isAdminSetupGuideOpen, onAdminSetupGuideOpen]);

  const {visibleContinueSetupTip, setPendingContinueSetupTip, clearRequestedContinueSetupTip} =
    useContinueSetupTip(previousTasks, tasks, isAdminSetupGuideOpen);

  const onContinueSetupTipDismiss = () => {
    setPendingContinueSetupTip(undefined);
    clearRequestedContinueSetupTip();
  };

  return (
    <>
      <StyledButton
        ref={tipAnchor}
        className={props.className}
        onClick={() => {
          trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ADMIN_SETUP_GUIDE_OPEN);
          if (visibleContinueSetupTip) {
            openAdminSetupGuideSetupScreen();
          } else {
            openAdminSetupGuide();
          }
          onContinueSetupTipDismiss();
        }}
      >
        <StyledButtonContent
          size={VisualSizesEnum.MEDIUM}
          // TODO: Make this prop optional.
          variant={ButtonVariantsEnum.CUSTOM}
          // TODO: Make this prop optional.
          color={PaletteColorsEnum.GREY}
          isRounded
          leftIcon="rocketSmall"
        >
          <StyledLabelDiv>
            <Trans id="admin-setup-guide-topbar-button-label">Setup guide</Trans>
          </StyledLabelDiv>
        </StyledButtonContent>
      </StyledButton>

      <StyledInfoBubble
        position="bottom"
        anchor={tipAnchor}
        onRequestClose={onContinueSetupTipDismiss}
        isVisible={!!visibleContinueSetupTip}
        isStandalone
      >
        <InfoBubbleContent>
          <InfoBubblePill type={InfoBubblePillVariantsEnum.EDUCATION} />
          <StyledInfoBubbleTitleDiv>
            <Trans id="admin-setup-guide-topbar-button-info-bubble-title">🚀 Continue setup</Trans>
          </StyledInfoBubbleTitleDiv>
          <InfoBubbleDescription>{renderContinueSetupTip(visibleContinueSetupTip)}</InfoBubbleDescription>
        </InfoBubbleContent>
        <StyledCloseButton onClick={onContinueSetupTipDismiss}>
          <StyledCloseIcon name="close" />
        </StyledCloseButton>
      </StyledInfoBubble>
      {isAdminSetupGuideOpen && (
        <AdminSetupGuideContainer
          panelId={panelId}
          tasks={tasksSuspendedUntilOpen}
          defaultScreen={computeDefaultScreen(tasks, defaultAdminSetupGuideScreen)}
          onDismissPermanentlyClick={() => {
            closeAdminSetupGuide();
            setIsDismissPermanentlyDialogOpen(true);
          }}
          onClose={() => {
            closeAdminSetupGuide();
          }}
        />
      )}
      {isDismissPermanentlyDialogOpen && (
        <OkCancelDialog
          okButtonVariant={ButtonVariantsEnum.DANGER}
          title={<Trans id="admin-setup-guide-dismiss-permanently-dialog-title">Dismiss setup guide</Trans>}
          okButtonLabel={<Trans id="admin-setup-guide-dismiss-permanently-dialog-confirm">Delete</Trans>}
          onRequestClose={() => {
            setIsDismissPermanentlyDialogOpen(false);
            openAdminSetupGuide();
          }}
          onOkClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.ADMIN_SETUP_GUIDE_DISMISS_PERMANENTLY,
            );
            dispatch(
              saveUserPreferencesAction({
                activationPreferences: {
                  ...activationPreferences,
                  isAdminSetupGuideDismissed: true,
                },
              }),
            );
            setIsDismissPermanentlyDialogOpen(false);
          }}
        >
          <Trans id="admin-setup-guide-dismiss-permanently-dialog-content">
            This will permanently remove the setup guide for you. This action cannot be undone.
          </Trans>
        </OkCancelDialog>
      )}
    </>
  );
};

function renderContinueSetupTip(continueSetupTip: ContinueSetupTipsEnum | undefined): ReactNode {
  if (!continueSetupTip) {
    return null;
  }

  switch (continueSetupTip) {
    case ContinueSetupTipsEnum.COMPLETED_SHARED_WORKSPACE:
      return (
        <Trans id="admin-setup-guide-topbar-button-info-bubble-description">
          You have completed a setup step. Open the setup guide for next steps.
        </Trans>
      );

    case ContinueSetupTipsEnum.CONNECT_CHANNEL:
      return (
        <Trans id="admin-setup-guide-topbar-button-info-bubble-connect-channel-description">
          Good job connecting a channel. Open the setup guide for next steps.
        </Trans>
      );

    case ContinueSetupTipsEnum.INVITE_TEAMMATES:
      return (
        <Trans id="admin-setup-guide-topbar-button-info-bubble-invite-teammates-description">
          Good job inviting teammates. Open the setup guide for next steps.
        </Trans>
      );

    default:
      assertUnreachable(continueSetupTip);
      return null;
  }
}

const useQueryLmsCourses = () => {
  const {data: completedCourses, refetch: refetchCompletedCourses} = useAppQuery(
    lmsCompletedCoursesQueries.forCourseUuids(courseUuids),
  );
  usePushEvent(PushEventTypesEnum.LMS_COURSE_COMPLETED, ApiCompletedCourse, () => refetchCompletedCourses());
  return {
    completedCourses,
    refetchCompletedCourses,
  };
};

const getTask = (tasks: ReadonlyArray<AdminSetupGuideScreenTask>, key: React.Key) =>
  tasks.find((task) => task.key === key);

// Show a tip "pulling" the user back into the setup guide at certain important cues.
const useContinueSetupTip = (
  previousTasks: AdminSetupGuideTasksAndProgress | null,
  tasks: AdminSetupGuideTasksAndProgress,
  isAdminSetupGuideOpen: boolean,
) => {
  const [pendingContinueSetupTip, setPendingContinueSetupTip] = useState<ContinueSetupTipsEnum | undefined>(
    undefined,
  );

  const previousSharedWorkspaceTask = getTask(previousTasks?.setup.tasks ?? [], sharedWorkspaceTask.key);
  const currentSharedWorkspaceTask = getTask(tasks.setup.tasks, sharedWorkspaceTask.key);
  const justCompletedSharedWorkspaceTask =
    previousSharedWorkspaceTask?.isComplete === false && currentSharedWorkspaceTask?.isComplete === true;

  const previousConnectChannelTask = getTask(previousTasks?.setup.tasks ?? [], connectChannelTask.key);
  const currentConnectChannelTask = getTask(tasks.setup.tasks, connectChannelTask.key);
  const justCompletedCurrentConnectChannelTask =
    previousConnectChannelTask?.isComplete === false && currentConnectChannelTask?.isComplete === true;

  const previousInviteTeammatesTask = getTask(previousTasks?.setup.tasks ?? [], inviteTeammatesTask.key);
  const currentInviteTeammatesTask = getTask(tasks.setup.tasks, inviteTeammatesTask.key);
  const justCompletedInviteTeammatesTask =
    previousInviteTeammatesTask?.isComplete === false && currentInviteTeammatesTask?.isComplete === true;

  useEffect(() => {
    if (isAdminSetupGuideOpen) {
      return;
    }

    if (justCompletedSharedWorkspaceTask) {
      setPendingContinueSetupTip(ContinueSetupTipsEnum.COMPLETED_SHARED_WORKSPACE);
    } else if (justCompletedCurrentConnectChannelTask) {
      setPendingContinueSetupTip(ContinueSetupTipsEnum.CONNECT_CHANNEL);
    } else if (justCompletedInviteTeammatesTask) {
      setPendingContinueSetupTip(ContinueSetupTipsEnum.INVITE_TEAMMATES);
    }
  }, [
    isAdminSetupGuideOpen,
    setPendingContinueSetupTip,
    justCompletedSharedWorkspaceTask,
    justCompletedCurrentConnectChannelTask,
    justCompletedInviteTeammatesTask,
  ]);

  const {requestedContinueSetupTip, clearRequestedContinueSetupTip} = useOnboardingModalState();

  // We show the tip when (1) we saw the state transition and (2) it was requested by some other part of the UI.
  const visibleContinueSetupTip =
    pendingContinueSetupTip === requestedContinueSetupTip ? requestedContinueSetupTip : undefined;

  return {
    visibleContinueSetupTip,
    setPendingContinueSetupTip,
    clearRequestedContinueSetupTip,
  };
};

const computeDefaultScreen = (
  tasks: AdminSetupGuideTasksAndProgress,
  defaultAdminSetupGuideScreen: AdminSetupGuideScreensEnum,
) => {
  // If we just closed the resume setup guide tip, we always want to land on the setup tab.
  // If we're opening the setup guide from a sandbox demo, always land on the demos tab.
  if (defaultAdminSetupGuideScreen !== AdminSetupGuideScreensEnum.DEFAULT) {
    return defaultAdminSetupGuideScreen;
  }

  if (tasks.demos.progress.hasCompletedAllTasks && tasks.setup.progress.hasCompletedAllTasks) {
    return AdminSetupGuideScreensEnum.MORE;
  }

  if (tasks.demos.progress.hasCompletedAllTasks) {
    return AdminSetupGuideScreensEnum.SETUP;
  }

  return AdminSetupGuideScreensEnum.DEMOS;
};
